import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Expands/collapses a clamped block of long-form text (e.g. collection SEO
 * copy) and keeps the toggle button, its label and `aria-expanded` in sync.
 *
 * The clamp stays in the DOM at its full height at all times — only its
 * `max-height` changes — so the text remains crawlable and present in page
 * source regardless of JavaScript. Without JavaScript the toggle stays
 * hidden and the clamp keeps its collapsed height; that trade-off is
 * deliberate (see sections/seo-text.liquid).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} clamp
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} toggleLabel
 *
 * @extends {Component<Refs>}
 */
class SeoTextComponent extends Component {
  requiredRefs = ['clamp', 'toggle', 'toggleLabel'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();

    const { toggle, clamp } = this.refs;
    toggle.addEventListener('click', this.#onToggle);
    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(clamp);
    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.toggle?.removeEventListener('click', this.#onToggle);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Shows or hides the toggle depending on whether the clamp is actually
   * truncating its content. Runs on connect and whenever the clamp's box
   * resizes (a merchant edit in the theme editor, a viewport change).
   */
  #measure = () => {
    const { clamp, toggle } = this.refs;
    if (this.#expanded) return;

    const overflowing = clamp.scrollHeight > clamp.clientHeight + 1;
    this.toggleAttribute('data-overflowing', overflowing);
    toggle.hidden = !overflowing;
  };

  #onToggle = () => {
    if (this.#expanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  /**
   * Reveals the full clamp height. `max-height` cannot transition to/from
   * `auto`, so the target height is measured (`scrollHeight`) and set
   * inline, then swapped to `none` once the transition finishes so later
   * content changes (e.g. an accordion opening inside) are not re-clamped.
   */
  #expand() {
    const { clamp, toggle, toggleLabel } = this.refs;
    this.#expanded = true;
    this.setAttribute('data-expanded', '');
    toggle.setAttribute('aria-expanded', 'true');
    toggleLabel.textContent = toggle.dataset.readLess ?? toggleLabel.textContent;

    if (prefersReducedMotion()) {
      clamp.style.maxHeight = 'none';
      return;
    }

    clamp.style.maxHeight = `${clamp.scrollHeight}px`;
    clamp.addEventListener(
      'transitionend',
      () => {
        clamp.style.maxHeight = 'none';
      },
      { once: true }
    );
  }

  /**
   * Re-clamps the content. The inline `max-height` is set back to the full
   * scroll height first (removing `none` would otherwise jump instantly),
   * then reset to the CSS custom property on the next frame so the
   * transition has a defined starting point.
   */
  #collapse() {
    const { clamp, toggle, toggleLabel } = this.refs;
    this.#expanded = false;
    clamp.style.maxHeight = `${clamp.scrollHeight}px`;

    requestAnimationFrame(() => {
      this.removeAttribute('data-expanded');
      toggle.setAttribute('aria-expanded', 'false');
      toggleLabel.textContent = toggle.dataset.readMore ?? toggleLabel.textContent;
      clamp.style.maxHeight = '';
    });
  }
}

if (!customElements.get('seo-text-component')) {
  customElements.define('seo-text-component', SeoTextComponent);
}
